import React, { useState, useEffect } from 'react';
import throttle from 'lodash/throttle';
import defer from 'lodash/defer';
import { GROUP_BUTTON_SIZES } from 'ui/buttonGroup';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { Add16, Subtract } from 'ui/icons';
import { GraphCommonEvent, ViewportChangeType } from '../../constants';
import { useFlowChart } from '../../hooks';
import { UnknownFunction, ViewPortEvent } from '../../types';
import { StyledButtonGroup, StyledButton, ZoomButton } from './FlowZoom.styles';

export const ZOOM_IN_TEST_ID = 'zoom-in';
export const ZOOM_OUT_TEST_ID = 'zoom-out';
export const ZOOM_TRIGGER_BUTTON_TEST_ID = 'zoom-trigger-button';

const convertToDecimal = (currentZoom: number) => currentZoom / 100;

const FlowZoomCmp = () => {
  const { fitView, getZoomPercentage, zoomTo, graph } = useFlowChart();
  const [zoom, setZoom] = useState(getZoomPercentage());
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const updateZoomValue = () => setZoom(getZoomPercentage());

  /**
   * Runs a given function, then updates the zoom state
   * @param {function} The function to run prior to updating the zoom state
   */
  const runZoomFn = (fn: UnknownFunction) => () => {
    fn();
    /**
     * `getZoomPercentage` should run after the canvas is done zooming to avoid
     * retrieving the previous zoom value, so `defer` is used.
     */
    defer(updateZoomValue);
  };

  useEffect(() => {
    const onViewportChange = throttle(
      ({ action }: ViewPortEvent) => action === ViewportChangeType.Zoom && updateZoomValue(),
      150,
      { leading: true, trailing: true },
    );
    graph?.on(GraphCommonEvent.onViewportChange, onViewportChange);

    return () => {
      graph?.off(GraphCommonEvent.onViewportChange, onViewportChange);
    };
  }, []);

  const zooms: Record<string, { zoomFn: UnknownFunction; label: string }> = {
    'zoom-200': { zoomFn: runZoomFn(() => zoomTo(2)), label: '200%' },
    'zoom-100': { zoomFn: runZoomFn(() => zoomTo(1)), label: '100%' },
    'zoom-75': { zoomFn: runZoomFn(() => zoomTo(0.75)), label: '75%' },
    'zoom-50': { zoomFn: runZoomFn(() => zoomTo(0.5)), label: '50%' },
    'zoom-25': { zoomFn: runZoomFn(() => zoomTo(0.25)), label: '25%' },
    'zoom-to-fit': { zoomFn: runZoomFn(fitView), label: 'Zoom to fit' },
  };

  const options = Object.entries(zooms).map(([id, option]) => ({
    hasFilter: true,
    id,
    isSelectMenu: true,
    label: option.label,
  }));

  const handleSelectionChange = (id: string) => {
    zooms[id]?.zoomFn?.();
    setIsMenuOpen(false);
  };

  return (
    <StyledButtonGroup id="zoom-button-groups" data-test="flowchart-zoom" trackSelected={false}>
      <StyledButton
        title="Zoom out"
        onSelect={runZoomFn(() => zoomTo(convertToDecimal(zoom - 10)))}
        data-test={ZOOM_OUT_TEST_ID}
        // @ts-ignore
        buttonSize={GROUP_BUTTON_SIZES.SMALL}
      >
        <Subtract />
      </StyledButton>
      <Menu
        isOpen={isMenuOpen}
        options={options}
        id="flowchart-zoom-menu"
        handleSelectionChange={handleSelectionChange}
        verticalAlignment={ALIGNMENT.UP}
      >
        <ZoomButton
          onSelect={() => setIsMenuOpen((state) => !state)}
          data-test={ZOOM_TRIGGER_BUTTON_TEST_ID}
        >{`${zoom}%`}</ZoomButton>
      </Menu>
      <StyledButton
        title="Zoom in"
        onSelect={runZoomFn(() => zoomTo(convertToDecimal(zoom + 10)))}
        data-test={ZOOM_IN_TEST_ID}
        // @ts-ignore
        buttonSize={GROUP_BUTTON_SIZES.SMALL}
      >
        <Add16 />
      </StyledButton>
    </StyledButtonGroup>
  );
};

export const FlowZoom = React.memo(FlowZoomCmp);

export default FlowZoom;
